/*
 * Slim summary view on the General tab. Replaces the inline ThemePicker
 * + PagesPicker grids with a current-state summary plus "Browse" buttons
 * that open the Theme Store / Page Store modals. Designed to scale: as
 * more themes / pages get added we don't keep growing this view.
 */

.summary {
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: var(--token-font-body);
}

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sectionHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.sectionTitle {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: 22px;
  color: var(--token-text-primary);
}

.sectionHint {
  margin: 0;
  font-size: 13px;
  color: rgba(28, 38, 32, 0.7);
  line-height: 1.4;
}

/* ---------- Theme summary ---------- */

.themeRow {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(28, 38, 32, 0.12);
  border-radius: 14px;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;
}

.themeRow:hover {
  background: rgba(255, 255, 255, 0.75);
  border-color: rgba(28, 38, 32, 0.2);
}

.themePreview {
  width: 96px;
  height: 56px;
  border-radius: 10px;
  flex-shrink: 0;
  background-color: #14241a;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
}

.themePreviewMascot {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  height: 70%;
}

.themeMeta {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.themeName {
  font-family: var(--token-font-display);
  font-size: 18px;
  color: var(--token-text-primary);
}

.themeNote {
  font-size: 12px;
  color: rgba(28, 38, 32, 0.62);
}

/* ---------- Show-mascot toggle ---------- */

.toggleRow {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(28, 38, 32, 0.12);
  border-radius: 14px;
  cursor: pointer;
}

.toggleText {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggleLabel {
  font-family: var(--token-font-display);
  font-size: 16px;
  color: var(--token-text-primary);
}

.toggleNote {
  font-size: 12px;
  color: rgba(28, 38, 32, 0.62);
}

/* Native checkbox styled as an iOS-ish pill switch. Off = neutral
 * grey; on = theme accent. */
.toggleSwitch {
  appearance: none;
  -webkit-appearance: none;
  width: 50px;
  height: 28px;
  border-radius: 999px;
  background: rgba(28, 38, 32, 0.22);
  position: relative;
  cursor: pointer;
  transition: background 120ms ease;
  flex-shrink: 0;
}

.toggleSwitch::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 120ms ease;
}

.toggleSwitch:checked {
  background: var(--token-accent);
}

.toggleSwitch:checked::after {
  transform: translateX(22px);
}

/* ---------- Page cards ---------- */

.pageGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.pageCard {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 14px 10px;
  background: rgba(255, 255, 255, 0.62);
  border: 1.5px solid rgba(28, 38, 32, 0.12);
  border-radius: 14px;
  text-align: left;
  transition:
    border-color 120ms ease,
    background 120ms ease;
}

.pageCard:hover {
  background: rgba(255, 255, 255, 0.88);
  border-color: rgba(28, 38, 32, 0.18);
}

.pageCardHead {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pageGlyph {
  font-size: 26px;
  line-height: 1;
  flex-shrink: 0;
}

.pageLabel {
  font-family: var(--token-font-display);
  font-size: 17px;
  color: var(--token-text-primary);
  flex: 1;
  min-width: 0;
}

.pageBlurb {
  margin: 0;
  font-size: 12px;
  color: rgba(28, 38, 32, 0.68);
  line-height: 1.35;
  min-height: 32px;
}

.pageControls {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px dashed rgba(28, 38, 32, 0.12);
}

.controlButton {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(28, 38, 32, 0.16);
  background: rgba(255, 255, 255, 0.85);
  color: rgba(28, 38, 32, 0.78);
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  font-family: var(--token-font-ui);
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.controlButton:hover:not(:disabled) {
  background: #fff;
  color: var(--token-text-primary);
}

.controlButton:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.removeButton {
  margin-left: auto;
  color: rgba(176, 58, 46, 0.8);
  border-color: rgba(176, 58, 46, 0.3);
}

.removeButton:hover:not(:disabled) {
  background: rgba(176, 58, 46, 0.08);
  color: #b03a2e;
  border-color: rgba(176, 58, 46, 0.55);
}

.empty {
  margin: 0;
  font-size: 13px;
  color: rgba(28, 38, 32, 0.55);
  font-style: italic;
  padding: 8px 4px;
}

/* ---------- Action buttons (Browse / Add) ---------- */

.actionButton {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid var(--token-accent);
  background: var(--token-accent);
  color: #fff;
  cursor: pointer;
  transition:
    background 120ms ease,
    transform 120ms ease;
  flex-shrink: 0;
}

.actionButton:hover {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
}

/* ---------- Grade-range segmented control ---------- */

.segmented {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.segment {
  flex: 1 1 220px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1.5px solid rgba(8, 18, 12, 0.16);
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease;
}

.segment:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--token-accent) 55%, transparent);
}

.segment:disabled {
  cursor: default;
  opacity: 0.7;
}

.segmentActive {
  border-color: var(--token-accent);
  background: color-mix(in srgb, var(--token-accent) 12%, rgba(255, 255, 255, 0.6));
}

.segmentLabel {
  font-family: var(--token-font-display);
  font-size: 18px;
  color: var(--token-text-primary);
}

.segmentNote {
  font-size: 12px;
  color: rgba(28, 38, 32, 0.62);
}

.gradeError {
  margin: 10px 0 0;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(255, 235, 220, 0.92);
  border: 1.5px solid var(--token-accent);
  color: var(--token-text-primary);
  font-size: 13px;
}
